import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Search, X, Tag as TagIcon } from "lucide-react";

const SOURCES = ["website", "youtube", "facebook", "instagram", "manual", "purchase"];
const STATUSES = ["active", "unsubscribed", "blocked"];
const TAG_OPTIONS = [
  "Music Subscriber",
  "Power BI Subscriber",
  "E-book Subscriber",
  "Merch Subscriber",
  "VIP",
];

const STATUS_STYLES = {
  active:       "border-emerald-500/40 text-emerald-300 bg-emerald-900/20",
  unsubscribed: "border-amber-500/40 text-amber-300 bg-amber-900/20",
  blocked:      "border-red-500/40 text-red-300 bg-red-900/20",
};
const SOURCE_STYLES = {
  website:   "border-sky-500/40 text-sky-300",
  youtube:   "border-red-500/40 text-red-300",
  facebook:  "border-blue-500/40 text-blue-300",
  instagram: "border-pink-500/40 text-pink-300",
  manual:    "border-neutral-500/40 text-neutral-300",
  purchase:  "border-[#D4AF37]/40 text-[#D4AF37]",
};

export default function SubscribersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSource, setFilterSource] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSource) params.source = filterSource;
      if (filterStatus) params.status = filterStatus;
      const { data } = await api.get("/admin/subscribers", { params });
      setRows(data);
    } catch (e) { toast.error("Failed to load subscribers"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterSource, filterStatus]);

  const update = async (email, patch) => {
    try {
      await api.patch(`/admin/subscribers/${encodeURIComponent(email)}`, patch);
      setRows((prev) => prev.map((r) => r.email === email ? { ...r, ...patch } : r));
      toast.success("Updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    try {
      await api.delete(`/admin/subscribers/${encodeURIComponent(email)}`);
      setRows((prev) => prev.filter((r) => r.email !== email));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const toggleTag = (row, tag) => {
    const has = (row.tags || []).includes(tag);
    const tags = has ? row.tags.filter((t) => t !== tag) : [...(row.tags || []), tag];
    update(row.email, { tags });
  };

  const filtered = q
    ? rows.filter((r) =>
        r.email.toLowerCase().includes(q.toLowerCase()) ||
        (r.name || "").toLowerCase().includes(q.toLowerCase()) ||
        (r.tags || []).some((t) => t.toLowerCase().includes(q.toLowerCase())))
    : rows;

  return (
    <div className="mt-8" data-testid="subscribers-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="text-sm text-neutral-400">
          {filtered.length} subscriber{filtered.length === 1 ? "" : "s"}
        </div>
        <button data-testid="add-subscriber-btn" onClick={() => setShowAdd(true)} className="btn-gold px-4 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
          <Plus size={14} /> Add Subscriber
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input data-testid="subs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / tag..." className="w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none pl-9 pr-3 py-2 rounded text-sm text-white" />
        </div>
        <div className="flex flex-wrap gap-2">
          <select data-testid="subs-filter-source" value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="bg-black border border-white/15 text-white text-xs tracking-widest uppercase px-3 py-2 rounded">
            <option value="">All Sources</option>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="subs-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-black border border-white/15 text-white text-xs tracking-widest uppercase px-3 py-2 rounded">
            <option value="">All Statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-neutral-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs tracking-widest uppercase text-neutral-400">
                <th className="text-left py-3">Name</th>
                <th className="text-left">Email</th>
                <th>Source</th>
                <th>Status</th>
                <th className="text-left">Tags</th>
                <th className="text-right">Subscribed</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.email} data-testid={`sub-row-${r.email}`} className="border-b border-white/5">
                  <td className="py-3 text-white">{r.name || <span className="text-neutral-600">—</span>}</td>
                  <td className="text-neutral-300">{r.email}</td>
                  <td>
                    <select data-testid={`sub-source-${r.email}`} value={r.source || "website"} onChange={(e) => update(r.email, { source: e.target.value })}
                      className={`bg-black border text-[10px] tracking-widest uppercase px-2 py-1 rounded ${SOURCE_STYLES[r.source] || SOURCE_STYLES.website}`}>
                      {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    <select data-testid={`sub-status-${r.email}`} value={r.status || "active"} onChange={(e) => update(r.email, { status: e.target.value })}
                      className={`bg-black border text-[10px] tracking-widest uppercase px-2 py-1 rounded ${STATUS_STYLES[r.status] || STATUS_STYLES.active}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {TAG_OPTIONS.map((t) => {
                        const active = (r.tags || []).includes(t);
                        return (
                          <button key={t} data-testid={`sub-tag-${r.email}-${t.replace(/\s/g, "-")}`} onClick={() => toggleTag(r, t)}
                            className={`px-2 py-0.5 text-[9px] tracking-widest uppercase rounded border ${active ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-neutral-400 hover:text-white"}`}>
                            {active ? t : `+ ${t}`}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="text-neutral-500 text-xs text-right whitespace-nowrap">
                    {r.subscribed_at ? new Date(r.subscribed_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="text-right">
                    <button data-testid={`sub-delete-${r.email}`} onClick={() => remove(r.email)} className="text-neutral-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={7} className="py-8 text-neutral-500 text-center">No subscribers match your filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddSubscriberModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddSubscriberModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", source: "manual", status: "active", tags: [] });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setSaving(true);
    try {
      await api.post("/admin/subscribers", form);
      toast.success("Subscriber added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleTag = (t) => setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="add-subscriber-modal">
      <form onSubmit={submit} className="bg-[#0F0F0F] border border-white/10 rounded-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif-display text-2xl text-white">Add Subscriber</h3>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <Field label="Email"><input data-testid="new-sub-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" placeholder="name@email.com" /></Field>
        <Field label="Name"><input data-testid="new-sub-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" placeholder="Display name" /></Field>
        <Field label="Source">
          <select data-testid="new-sub-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="inp">
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select data-testid="new-sub-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="inp">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Tags">
          <div className="flex flex-wrap gap-1 mt-1">
            {TAG_OPTIONS.map((t) => (
              <button type="button" key={t} onClick={() => toggleTag(t)}
                className={`px-2 py-0.5 text-[10px] tracking-widest uppercase rounded border ${form.tags.includes(t) ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-neutral-400 hover:text-white"}`}>
                {form.tags.includes(t) ? t : `+ ${t}`}
              </button>
            ))}
          </div>
        </Field>
        <div className="mt-6 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs tracking-widest uppercase text-neutral-400 hover:text-white">Cancel</button>
          <button type="submit" data-testid="save-subscriber" disabled={saving} className="btn-gold px-4 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <TagIcon size={14} />} Save
          </button>
        </div>
        <style>{`
          .inp { width:100%; background:#000; border:1px solid rgba(255,255,255,0.15); padding:0.5rem 0.75rem; border-radius:0.375rem; color:#fff; font-size:0.875rem; outline:none; }
          .inp:focus { border-color:#D4AF37; }
        `}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mt-3">
      <label className="text-[10px] tracking-widest uppercase text-neutral-400">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
